import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type PaymentMethod = "EFECTIVO" | "TRANSFERENCIA" | "CHEQUE" | "TARJETA" | "OTRO";
export type PaymentStatus = "REGISTRADO" | "ANULADO";

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  EFECTIVO: "Efectivo",
  TRANSFERENCIA: "Transferencia",
  CHEQUE: "Cheque",
  TARJETA: "Tarjeta",
  OTRO: "Otro",
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  REGISTRADO: "Registrado",
  ANULADO: "Anulado",
};

export interface PaymentAllocationRecord {
  id: string;
  invoiceId: string;
  amountApplied: string;
  invoice?: { id: string; invoiceNumber: number; total: string; status: string; dueDate: string } | null;
}

export interface PaymentRecord {
  id: string;
  clientId: string;
  paymentNumber: number;
  paymentDate: string;
  amount: string;
  method: PaymentMethod;
  reference: string | null;
  notes: string | null;
  status: PaymentStatus;
  voidedAt: string | null;
  createdAt: string;
  allocations: PaymentAllocationRecord[];
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
  createdBy?: { id: string; fullName: string; email: string } | null;
}

export interface ListPaymentsFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: PaymentStatus;
  dateFrom?: string;
  dateTo?: string;
}

export function usePayments(filters: ListPaymentsFilters = {}) {
  return useQuery({
    queryKey: ["payments", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PaymentRecord[]>>("/payments", { params: filters });
      return data;
    },
  });
}

export interface PaymentAllocationPayload {
  invoiceId: string;
  amount: number | string;
}

export interface CreatePaymentPayload {
  clientId: string;
  paymentDate: string;
  amount: number | string;
  method: PaymentMethod;
  reference?: string;
  notes?: string;
  allocations: PaymentAllocationPayload[];
}

export function useCreatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePaymentPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PaymentRecord>>("/payments", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["cartera-aging"] });
    },
  });
}

export function useVoidPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PaymentRecord>>(`/payments/${id}/void`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["cartera-aging"] });
    },
  });
}

export function paymentClientName(payment: PaymentRecord): string | null {
  if (!payment.client) return null;
  return payment.client.clientType === "PERSONA_JURIDICA"
    ? payment.client.businessName || payment.client.tradeName || "Cliente"
    : payment.client.fullName || "Cliente";
}

export interface AgingBucketRow {
  clientId: string;
  clientName: string;
  current: number;
  days1To30: number;
  days31To60: number;
  days61To90: number;
  daysOver90: number;
  totalOutstanding: number;
  invoiceCount: number;
}

export interface AgingReport {
  rows: AgingBucketRow[];
  totals: {
    current: number;
    days1To30: number;
    days31To60: number;
    days61To90: number;
    daysOver90: number;
    totalOutstanding: number;
  };
}

export function useAgingReport() {
  return useQuery({
    queryKey: ["cartera-aging"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AgingReport>>("/cartera/aging");
      return data.data;
    },
  });
}
